/**
 * The plant's limits (Stage 2 of docs/plans/2026-09-27_ai-report-on-the-brief-engine.md): the process windows and the
 * scrap targets a process engineer sets. GET is a member's read; PUT is a model writer's (model.write), stamped with
 * who set it and when, and queues an analysis refresh (reason limits) that reads the plant's days again against it. A
 * limit is a person's, never the analysis's or the consultant's, which only suggest (BriefDriver.range). The Go mirror
 * is platform-core/internal/twin/process_window.go and scrap_targets.go.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, ApiClientError } from "@/lib/api-client";
import { BRIEF_LIST_KEY, CADENCE_BRIEF_KEY, useBriefLive } from "@/lib/api/brief";

/** A condition's operating window as set: absent ends are open (a floor has no hi, a ceiling no lo). */
export interface ProcessWindow {
  condition: string;
  /** One grade's window; absent for every grade. */
  grade?: string;
  lo?: number;
  hi?: number;
  unit: string;
  note: string;
  set_by: string;
  set_by_name: string;
  set_at: string;
  superseded_at?: string;
  superseded_by?: string;
  /** What it was set from (migration 0193); absent for a window set before it was recorded. */
  basis?: LimitBasisKind;
  /** What the basis rests on, copied when it was set (the suggestion, the card, the file and row, the band). */
  basis_detail?: Record<string, unknown>;
  /** The reaction plan (migration 0250, B3 T4): what to do when the value leaves the window, and who does it. */
  reaction?: string;
  reaction_owner?: string;
  /**
   * The other names that fold to the condition (the priors' aliases), on an open window: an excursion or a control
   * plan row named by any of them reads this window's reaction plan, as the server reads it.
   */
  aliases?: string[];
}

/** What a limit was set from: typed by a person, the analysis's suggestion, a consultant's card, a spreadsheet or a tag's band. */
export type LimitBasisKind = "typed" | "suggestion" | "consultant" | "import" | "tag_band";

/**
 * What a write says it was set from: the suggestion's result (a window only), the card's proposal, the spreadsheet's
 * file and row, or the parameter's tag band (a window only), which the platform reads itself and takes only where the
 * plant owns it. The platform copies the numbers it rests on.
 */
export interface LimitBasis {
  kind: LimitBasisKind;
  result_id?: string;
  proposal_id?: string;
  file?: string;
  row?: number;
}

export interface ProcessWindows {
  windows: ProcessWindow[];
  history: ProcessWindow[];
}

/** A scrap target as set: a defect's (by the briefs' key) or the total's. */
export interface ScrapTarget {
  kind: "total" | "defect";
  defect?: string;
  target_rate: number;
  note: string;
  set_by: string;
  set_by_name: string;
  set_at: string;
  superseded_at?: string;
  superseded_by?: string;
  /** What it was set from (0193): typed, a consultant's card or a spreadsheet; absent before it was recorded. */
  basis?: LimitBasisKind;
  basis_detail?: Record<string, unknown>;
}

export interface ScrapTargets {
  targets: ScrapTarget[];
  history: ScrapTarget[];
}

/** A window's write: its ends (one may be absent), or clear. */
export interface ProcessWindowWrite {
  condition: string;
  grade?: string;
  lo?: number;
  hi?: number;
  unit?: string;
  note?: string;
  clear?: boolean;
  basis?: LimitBasis;
  /** The reaction plan: absent keeps the open window's, "" clears it. */
  reaction?: string;
  reaction_owner?: string;
}

/** A target's write: its rate as a fraction, or none to clear it. */
export interface ScrapTargetWrite {
  kind: "total" | "defect";
  defect?: string;
  target_rate?: number;
  note?: string;
  basis?: LimitBasis;
}

/** A spreadsheet's windows at once (PUT process-windows/batch): the file and each row, as it came. */
export interface ProcessWindowBatch {
  file: string;
  windows: (Omit<ProcessWindowWrite, "clear" | "basis"> & { row: number })[];
}

/** A batch's answer: the windows after it, and the rows applied and left as they were. */
export interface ProcessWindowBatchResult extends ProcessWindows {
  applied: number;
  unchanged: number;
}

/** A row of a batch the platform refused, in its words (400 invalid_rows: nothing was saved). */
export interface ProcessWindowRowError {
  row: number;
  condition: string;
  error: string;
}

const list = <T>(v: T[] | null | undefined): T[] => v ?? [];

/** The windows as the page reads them: a Go nil list as empty. */
export function processWindowsOf(raw: Partial<ProcessWindows> | null | undefined): ProcessWindows {
  return { windows: list(raw?.windows), history: list(raw?.history) };
}

/** The targets as the page reads them: a Go nil list as empty. */
export function scrapTargetsOf(raw: Partial<ScrapTargets> | null | undefined): ScrapTargets {
  return { targets: list(raw?.targets), history: list(raw?.history) };
}

export const PROCESS_WINDOWS_KEY = ["process-windows"] as const;
export const SCRAP_TARGETS_KEY = ["scrap-targets"] as const;
/**
 * The grade chemistry document's key (lib/api/grade-chemistry.ts, P5): a grade's minimum and maximum are its process
 * windows, so a window saved here makes that read stale. Kept here so the two modules share it without a cycle.
 */
export const GRADE_CHEMISTRY_KEY = ["grade-chemistry"] as const;

/**
 * What a saved window makes stale beside its own answer: the Grades section and the product sheets (their limits are
 * these windows), and the briefs, read again once the refresh the save queued has run.
 */
export function windowsSaved(qc: Pick<QueryClient, "invalidateQueries">): void {
  void qc.invalidateQueries({ queryKey: GRADE_CHEMISTRY_KEY });
  void qc.invalidateQueries({ queryKey: CADENCE_BRIEF_KEY });
  void qc.invalidateQueries({ queryKey: BRIEF_LIST_KEY });
}

/** Null where the platform does not serve the route yet (404), so the page says so and offers no editor. */
async function orNull<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read();
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return null;
    throw err;
  }
}

export function fetchProcessWindows(): Promise<ProcessWindows | null> {
  return orNull(async () => processWindowsOf(await api.get<ProcessWindows>("/v1/twin/process-windows")));
}

export function fetchScrapTargets(): Promise<ScrapTargets | null> {
  return orNull(async () => scrapTargetsOf(await api.get<ScrapTargets>("/v1/twin/scrap-targets")));
}

export async function saveProcessWindow(w: ProcessWindowWrite): Promise<ProcessWindows> {
  return processWindowsOf(await api.put<ProcessWindows>("/v1/twin/process-windows", w));
}

export async function saveScrapTarget(t: ScrapTargetWrite): Promise<ScrapTargets> {
  return scrapTargetsOf(await api.put<ScrapTargets>("/v1/twin/scrap-targets", t));
}

export async function saveProcessWindows(b: ProcessWindowBatch): Promise<ProcessWindowBatchResult> {
  const raw = await api.put<ProcessWindowBatchResult>("/v1/twin/process-windows/batch", b);
  return { ...processWindowsOf(raw), applied: raw.applied ?? 0, unchanged: raw.unchanged ?? 0 };
}

/** The rows of a batch the platform refused (its 400 invalid_rows), else none. */
export function batchRowErrorsOf(err: unknown): ProcessWindowRowError[] {
  if (!(err instanceof ApiClientError) || err.status !== 400) return [];
  const body = err.body as { error?: unknown; rows?: unknown } | null;
  return body?.error === "invalid_rows" && Array.isArray(body.rows) ? (body.rows as ProcessWindowRowError[]) : [];
}

export function useProcessWindowsQuery() {
  const live = useBriefLive();
  return useQuery({ queryKey: PROCESS_WINDOWS_KEY, queryFn: fetchProcessWindows, enabled: live, staleTime: 30_000 });
}

/**
 * One parameter the plant records (GET /v1/twin/process-windows/parameters, process_window_params.go), named as the
 * analysis names it: its unit (a unit's spellings one; a window's save reads the unit by the same rule), the other names
 * it is recorded under, where and at what grain it is read (ladle, heat, shift, mould; lot for a lot's condition, tag
 * for a live tag) and by which stations (their ids and names), the grades and heats that carry it, the spread of the
 * last days' readings, whether the briefs count it against a window, and a bound tag's band (a reference, never a
 * window: spec_source says whose it is).
 */
export interface ProcessWindowParameter {
  condition: string;
  unit: string;
  grain: string;
  recorded?: string[];
  stations: string[];
  /** The stations' names, for the page's search (an id where the station has no name; none from an older platform). */
  station_names?: string[];
  grades: string[];
  readings: number;
  heats: number;
  spread?: { p05: number; median: number; p95: number; min: number; max: number };
  counted: boolean;
  /** A bound tag's band, a reference: whose it is (spec_source) and the tag it is read from. */
  tag_band?: { lo?: number; hi?: number; spec_source: string; tag_id?: string; tag?: string };
}

/**
 * Every parameter, the days their counts and spreads read, and names_cut: the plant recorded more names per shift or per
 * mould in those days than the list holds (500).
 */
export interface ProcessWindowParameters {
  parameters: ProcessWindowParameter[];
  days: number;
  names_cut?: boolean;
}

export const PROCESS_WINDOW_PARAMETERS_KEY = ["process-window-parameters"] as const;

/** The parameters as the page reads them: a Go nil list as empty. */
export function processWindowParametersOf(raw: Partial<ProcessWindowParameters> | null | undefined): ProcessWindowParameters {
  return {
    parameters: list(raw?.parameters).map((p) => ({
      ...p,
      stations: list(p.stations),
      station_names: list(p.station_names),
      grades: list(p.grades),
    })),
    days: raw?.days ?? 180,
    names_cut: raw?.names_cut ?? false,
  };
}

/** Every parameter the plant records; null where the platform does not serve the list yet. */
export function fetchProcessWindowParameters(): Promise<ProcessWindowParameters | null> {
  return orNull(async () =>
    processWindowParametersOf(await api.get<ProcessWindowParameters>("/v1/twin/process-windows/parameters")),
  );
}

export function useProcessWindowParametersQuery() {
  const live = useBriefLive();
  return useQuery({
    queryKey: PROCESS_WINDOW_PARAMETERS_KEY,
    queryFn: fetchProcessWindowParameters,
    enabled: live,
    staleTime: 5 * 60_000,
  });
}

/**
 * What the page says when a window or a target is not saved: the platform's own words where it refused the values (a
 * parameter the plant does not record, another unit, the window or target itself, what it names as set from), what to
 * do where the suggestion it started from is gone or the page's request could not be read (#1089 review, LOW 1), a
 * note where another save won the same moment, else a general word.
 */
export function limitsSaveErrorText(err: unknown): string {
  if (err instanceof ApiClientError) {
    const body = err.body as { error?: unknown; detail?: unknown } | null;
    const detail = typeof body?.detail === "string" ? body.detail : "";
    switch (body?.error) {
      case "unknown_condition":
      case "unit_mismatch":
      case "invalid_window":
      case "invalid_target":
      case "invalid_basis":
        if (detail) return `The platform did not save it: ${detail}.`;
        break;
      case "suggestion_gone":
        return "The analysis's suggestion this window started from is no longer stored: read the page again and save it once more.";
      case "invalid_body":
        return "The platform could not read what the page sent: read the page again and save it once more.";
      case "window_changed":
      case "target_changed":
        return "Someone saved this at the same moment. Read the page again, then save yours if it still stands.";
    }
  }
  return "The platform did not save it. Check the values and try again.";
}

export function useScrapTargetsQuery() {
  const live = useBriefLive();
  return useQuery({ queryKey: SCRAP_TARGETS_KEY, queryFn: fetchScrapTargets, enabled: live, staleTime: 30_000 });
}

/**
 * Saves a window (or clears it) and keeps the answer; the briefs are read again once the refresh it queued has run, so
 * their cached reads are marked stale now.
 */
export function useSaveProcessWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveProcessWindow,
    onSuccess: (doc) => {
      qc.setQueryData(PROCESS_WINDOWS_KEY, doc);
      windowsSaved(qc);
    },
  });
}

/** Saves a spreadsheet's windows at once, as useSaveProcessWindow. */
export function useSaveProcessWindows() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveProcessWindows,
    onSuccess: (doc) => {
      qc.setQueryData(PROCESS_WINDOWS_KEY, { windows: doc.windows, history: doc.history });
      windowsSaved(qc);
    },
  });
}

/** Saves a target (or clears it), as useSaveProcessWindow. */
export function useSaveScrapTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveScrapTarget,
    onSuccess: (doc) => {
      qc.setQueryData(SCRAP_TARGETS_KEY, doc);
      void qc.invalidateQueries({ queryKey: CADENCE_BRIEF_KEY });
      void qc.invalidateQueries({ queryKey: BRIEF_LIST_KEY });
    },
  });
}

/**
 * A gauge checked against an independent one (B7 T2, GET /v1/twin/process-windows/agreement, gauge_agreement.go): a
 * parameter measured by two tags bound on different equipment in one unit, their days paired (each day of the sparser
 * source with the nearest of the other within pair_days plant days) and compared as Bland and Altman compare two
 * methods, a minus b. The statistics and the words only where shown (at least min_pairs pairs).
 */
export interface GaugeSource {
  tag_id: string;
  tag: string;
  entity_id: string;
  entity: string;
  days: number;
}

export interface GaugeAgreement {
  condition: string;
  unit: string;
  a: GaugeSource;
  b: GaugeSource;
  n: number;
  from?: string;
  to?: string;
  pairing: string;
  shown: boolean;
  bias?: number;
  sd?: number;
  bias_lo?: number;
  bias_hi?: number;
  loa_lo?: number;
  loa_hi?: number;
  level: number;
  verdict?: string;
  limits?: string;
}

export interface GaugeAgreements {
  agreements: GaugeAgreement[];
  days: number;
  pair_days: number;
  min_pairs: number;
}

export const GAUGE_AGREEMENT_KEY = ["process-window-agreement"] as const;

/** The agreements as the page reads them: a Go nil list as empty. */
export function gaugeAgreementsOf(raw: Partial<GaugeAgreements> | null | undefined): GaugeAgreements {
  return {
    agreements: list(raw?.agreements),
    days: raw?.days ?? 365,
    pair_days: raw?.pair_days ?? 3,
    min_pairs: raw?.min_pairs ?? 8,
  };
}

/** Every parameter with two sources; null where the platform does not serve the agreement yet. */
export function fetchGaugeAgreements(): Promise<GaugeAgreements | null> {
  return orNull(async () => gaugeAgreementsOf(await api.get<GaugeAgreements>("/v1/twin/process-windows/agreement")));
}

export function useGaugeAgreementsQuery() {
  const live = useBriefLive();
  return useQuery({ queryKey: GAUGE_AGREEMENT_KEY, queryFn: fetchGaugeAgreements, enabled: live, staleTime: 5 * 60_000 });
}
